import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GAHAppResponse } from '@data/schema/gah-app-response';
import { environment } from '@environments/environment';
import { ApiPaths } from '@gah-core/constants/api-paths.enum';
import { NGXLogger } from 'ngx-logger';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
declare var AW4: any;

@Injectable({
  providedIn: 'root',
})
export class AsperaWebService {
  asperaWeb: any;
  config = {
    logLevel: 'WARN',
    apiConnectProxy: '',
    isFixedURL: false,
    fixedURL: '',
    isFixedConnectAuth: false,
    fixedConnectAuth: false,
    enableGoto: false,
    enableCredLocalStorage: true,
    defaultCred: {
      nodeURL: 'https://demo.asperasoft.com:9092',
      nodeUser: 'asperaweb',
      nodePW: 'demoaspera',
      useTokenAuth: false,
    },
    connectInstaller: '//d3gcli72yxqn2z.cloudfront.net/connect/v4',
  };
  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private spinner: NgxSpinnerService
  ) {}

  initAsperaConnect() {
    this.asperaWeb = new AW4.Connect({
      minVersion: '3.9.0',
      dragDropEnabled: true,
    });
    const asperaInstaller = new AW4.ConnectInstaller({
      sdkLocation: this.config.connectInstaller,
    });
    asperaInstaller.supportsInstallingExtensions = true;
    this.asperaWeb.addEventListener(AW4.Connect.EVENT.STATUS, (eventType, status) => {
      switch (status) {
        case AW4.Connect.STATUS.INITIALIZING:
          asperaInstaller.showLaunching();
          break;
        case AW4.Connect.STATUS.FAILED:
          asperaInstaller.showDownload();
          break;
        case AW4.Connect.STATUS.OUTDATED:
          asperaInstaller.showUpdate();
          break;
        case AW4.Connect.STATUS.RUNNING:
          asperaInstaller.connected();
          break;
      }
    });
    this.asperaWeb.initSession();
    this.asperaWeb.version({
      success: (data) => this.logger.log('Connect version: ', data),
      error: (err) => this.logger.error('connect.version CB ERROR: ', err.error),
    });
  }

  asperaRunnerUploadFiles(
    data: any,
    gpmsId: number,
    releaseId: number,
    groupTypeCode: string,
    territory: string,
    region: string,
    uploadedBy: string,
    territoryId: number,
    isReplaceAssetOperation: boolean,
    serviceResultOfReplacementApi: any,
  ): Observable<any> {
    this.spinner.show();
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.AsperaRunnerUpload}`, {
        files: data.dataTransfer.files,
        gpmsId: gpmsId,
        releaseId: releaseId,
        groupTypeCode: groupTypeCode,
        territory: territory,
        region: region,
        uploadedBy: uploadedBy,
        territoryId: territoryId,
        replaceAssetOperation: isReplaceAssetOperation
      })
      .pipe(
        map((res: GAHAppResponse) => {
          // Put the full local file paths into the transfer spec so Aspera knows
          // where to find them on the local client.
          var files = data.dataTransfer.files;
          for (var i = 0, length = files.length; i < length; i += 1) {
            res.serviceResult.transfer_spec.paths[i].source = files[i].name;
          }
          if(isReplaceAssetOperation) {
            //its an asset-repmacement hence passed configs of an
            //original asset to a plugin
            this.asperaWeb.startTransfer(
              serviceResultOfReplacementApi.transfer_spec,
              serviceResultOfReplacementApi.connection_settings
            );
          } else {
            // Initiate the transfer.
            this.asperaWeb.startTransfer(
              res.serviceResult.transfer_spec,
              res.serviceResult.connection_settings
            );
          }
          return res.serviceResult.asset_items;
        })
      );
  }

  asperaRunnerDownloadFiles(runnerId, proxyType: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.AsperaRunnerDownload}`, {
        runnerId,
        proxyType,
      })
      .pipe(
        map((res: GAHAppResponse) => {
          // Initiate aspera download
          for (var key in res.serviceResult.download_configurations) {
            this.asperaWeb.startTransfer(
              res.serviceResult.download_configurations[key],
              res.serviceResult.connection_settings
            );
          }
        })
      );
  }

  httpRunnerDownloadFiles(runnerId, proxyType: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.HttpRunnerDownload}`, {
        runnerId,
        proxyType,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  sharedAssethttpDownloadFiles(
    runnerId,
    proxyType: string,
    assetToken: string,
    downloadType: string
  ): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.SharedAssetBulkDownload}`, {
        runnerId,
        proxyType,
        assetToken,
        downloadType,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }
  downloadAssetBasedOnProxySelection(
    runnerId: number,
    proxyType: string,
    type: string
  ): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.DownloadAssetAsPerProxy}`, {
        runnerId: runnerId,
        proxyType: proxyType,
        downloadType: type,
      })
      .pipe(
        map((res: GAHAppResponse) => {
          // Initiate aspera download
          if (type == 'Aspera') {
            this.asperaWeb.startTransfer(
              res.serviceResult.download_configurations[0],
              res.serviceResult.connection_settings
            );
          } else {
            return res;
          }
        })
      );
  }

  downloadAssetViewBasedOnProxySelection(
    runnerId: number,
    proxyType: string,
    type: string,
    passtoken: string
  ): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.DownloadAssetAsPerProxyAssetView}`, {
        runnerId: runnerId,
        proxyType: proxyType,
        downloadType: type,
        passToken: passtoken,
      })
      .pipe(
        map((res: GAHAppResponse) => {
          // Initiate aspera download
          if (type == 'Aspera') {
            this.asperaWeb.startTransfer(
              res.serviceResult.download_configurations[0],
              res.serviceResult.connection_settings
            );
          } else {
            return res;
          }
        })
      );
  }

  asperaOrHttpRunnerDownload(runnerId, token: string, type: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}${ApiPaths.AssetDownload}`, {
        runnerId: runnerId,
        assetToken: token,
        type: type,
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse) {
    let errorMsg: {
      message: string;
      errorCode: number;
    } = {
      message: error.error.errorDescription,
      errorCode: error.status,
    };
    return throwError(errorMsg);
  }
}
